import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { World } from '@/types/core/World';
import type { Mixer } from '@/types/core/Mixer';
import type { Item, ItemRenderFunction } from '@/types/core/Item';
import * as THREE from 'three';
import { ItemDefinitions } from '@/types/items';
import { ItemDefinitionType } from '@/types/items';
import { Clock } from '@/types/core/Clock';
import { MeasureTypes } from '@/types/core/Clock';
/**
 * Type for the state of the app
 */
export interface AppState {
  worlds: Record<string, World>;
  activeWorldId: string | null;
  mixers: Record<string, Mixer>;
  activeMixerId: string | null;
  selectedItemId: string | null;
  clock: Clock | null;
}

/**
 * Type for the context
 */
interface AppContextType {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  createWorld: (name: string, mode: string) => void;
  setActiveWorld: (worldId: string | null) => void;
  setActiveMixer: (mixerId: string | null) => void;
  selectItem: (itemId: string | null) => void;
  addMesh: (subtype?: string, properties?: Record<string, any>) => void;
  addLight: (subtype?: string) => void;
  addEffect: (subtype?: string) => void;
  addLink: (subtype?: string) => void;
  addMaterial: (subtype?: string) => void;
  updateItem: (itemId: string, changes: Partial<Item>) => void;
  deleteItem: (itemId: string) => void;
  deleteChildItem: (parentId: string, childType: string, childIndex: number) => void;
  activeWorld: World | null;
  selectedItem: Item | null;
  definitions: typeof ItemDefinitions;
  getDefinition: (type: string) => (typeof ItemDefinitions)[ItemDefinitionType] | undefined;
  createItem: (type: string, name: string) => Item | null;
  clock: Clock | null;
  startClock: () => void;
  stopClock: () => void;
  setBpm: (bpm: number) => void;
  setMeasure: (measure: MeasureTypes) => void;
}

/**
 * Initial state for the app
 */
const initialState: AppState = {
  worlds: {},
  activeWorldId: null,
  mixers: {},
  activeMixerId: null,
  selectedItemId: null,
  clock: null
};

/**
 * Context for the app
 */
const AppContext = createContext<AppContextType | undefined>(undefined);

/**
 * Provider component for the AppContext
 * 
 * @param children - The children of the provider
 */
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>(initialState);
  const definitions = useMemo(() => ItemDefinitions, []);

  // Add computed properties
  const activeWorld = state.activeWorldId ? state.worlds[state.activeWorldId] : null;
  const selectedItem = state.selectedItemId && activeWorld ? activeWorld.items[state.selectedItemId] : null;
  const clock = state.clock;

  // Clock management functions
  const startClock = () => {
    if (!state.clock) {
      setState(prev => ({
        ...prev,
        clock: new Clock(120, '4/4', true)
      }));
    } else {
      state.clock.start();
      // Trigger re-render to update UI
      setState(prev => ({ ...prev }));
    }
  };

  const stopClock = () => {
    if (state.clock) {
      state.clock.reset();
      // Trigger re-render to update UI
      setState(prev => ({ ...prev }));
    }
  };

  const setBpm = (bpm: number) => {
    if (state.clock) {
      state.clock.setBpm(bpm);
      // Trigger re-render to update UI
      setState(prev => ({ ...prev }));
    }
  };

  const setMeasure = (measure: MeasureTypes) => {
    if (state.clock) {
      state.clock.setMeasure(measure);
      // Trigger re-render to update UI
      setState(prev => ({ ...prev }));
    }
  };

  const getDefinition = (type: string) => {
    return definitions[type as ItemDefinitionType];
  };

  const createItem = (type: string, name: string): Item | null => {
    // Get the item definition from the centralized ItemDefinitions object
    const baseItem = ItemDefinitions[type as ItemDefinitionType];
    
    if (!baseItem) {
      console.error(`Unknown item type: ${type}`);
      return null;
    }

    // Create a new item while preserving the render function
    return {
      ...baseItem,
      id: '',
      name,
      properties: { ...baseItem.properties },
      render: baseItem.render as ItemRenderFunction
    };
  };

  /**
   * Creates a new world
   * 
   * @param name - The name of the world to create
   */
  const createWorld = (name: string, mode: string) => {
    const worldId = uuidv4();
    const cameraId = uuidv4();
    const lightId = uuidv4();

    // Create default camera
    const camera = createItem('camera', 'Main Camera');

    if (!camera) {
      console.error('Failed to create default camera');
      return;
    }

    // Create default light
    const light = createItem('light.directional', 'Default Light');

    if (!light) {
      console.error('Failed to create default light');
      return;
    }

    const newWorld: World = {
      id: worldId,
      name,
      items: {
        [cameraId]: { ...camera, id: cameraId },
        [lightId]: { ...light, id: lightId }
      }
    };

    setState(prev => ({
      ...prev,
      worlds: {
        ...prev.worlds,
        [worldId]: newWorld
      },
      activeWorldId: worldId
    }));
  };

  /**
   * Sets the active world
   * 
   * @param worldId - The id of the world to set as active
   */
  const setActiveWorld = (worldId: string | null) => {
    setState(prev => ({
      ...prev,
      activeWorldId: worldId
    }));
  };

  /**
   * Sets the active mixer
   * 
   * @param mixerId - The id of the mixer to set as active
   */
  const setActiveMixer = (mixerId: string | null) => {
    setState(prev => ({
      ...prev,
      activeMixerId: mixerId
    }));
  };

  /**
   * Selects an item in the active world
   * 
   * @param itemId - The id of the item to select
   */
  const selectItem = (itemId: string | null) => {
    setState(prev => ({
      ...prev,
      selectedItemId: itemId
    }));
  };

  /**
   * Adds a mesh to the active world
   * 
   * @param subtype - The subtype of the mesh to add
   * @param properties - Additional properties to add to the mesh
   */
  const addMesh = (subtype: string = 'box', properties?: Record<string, any>) => {
    if (!activeWorld) return;

    setState(prev => {
      const meshId = uuidv4();
      const definitionType = `mesh.${subtype}` as ItemDefinitionType;
      const meshCount = Object.values(activeWorld.items).filter(item => item.type === 'mesh').length;
      const newMesh = createItem(definitionType, `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} ${meshCount + 1}`);

      if (!newMesh) {
        console.error(`Failed to create mesh of type ${subtype}`);
        return prev;
      }

      // Create a default material if none is provided
      const materialId = properties?.materialId || uuidv4();
      const defaultMaterialType = 'standard';
      const materialDefinitionType = `material.${defaultMaterialType}` as ItemDefinitionType;
      const materialCount = Object.values(activeWorld.items).filter(item => item.type === 'material').length;
      const defaultMaterial = createItem(
        materialDefinitionType, 
        `${defaultMaterialType.charAt(0).toUpperCase() + defaultMaterialType.slice(1)} Material ${materialCount + 1}`
      );

      if (!defaultMaterial) {
        console.error(`Failed to create default material of type ${defaultMaterialType}`);
        return prev;
      }

      // Initialize transform with new instances
      const defaultTransform = {
        position: new THREE.Vector3(0, 0, 0),
        rotation: new THREE.Euler(0, 0, 0),
        scale: new THREE.Vector3(1, 1, 1)
      };

      // Assign the material to the mesh and merge any additional properties
      const meshWithMaterial = {
        ...newMesh,
        id: meshId,
        properties: {
          ...newMesh.properties,
          ...properties,
          transform: defaultTransform,
          materialId
        }
      };

      // Only add the material if it doesn't already exist
      const updatedItems = {
        ...activeWorld.items,
        [meshId]: meshWithMaterial,
      };

      // Add the material if it's a new one (not an existing one)
      if (!properties?.materialId || !activeWorld.items[properties.materialId]) {
        updatedItems[materialId] = { ...defaultMaterial, id: materialId };
      }

      return {
        ...prev,
        worlds: {
          ...prev.worlds,
          [activeWorld.id]: {
            ...activeWorld,
            items: updatedItems
          }
        },
        selectedItemId: meshId
      };
    });
  };

  /**
   * Adds a light to the active world
   * 
   * @param subtype - The subtype of the light to add
   */
  const addLight = (subtype: string = 'point') => {
    if (!activeWorld) return;

    setState(prev => {
      const id = uuidv4();
      const lightCount = Object.values(activeWorld.items).filter(item => item.type === 'light').length;
      const definitionType = `light.${subtype}` as ItemDefinitionType;
      const newLight = createItem(definitionType, `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} ${lightCount + 1}`);

      if (!newLight) {
        console.error(`Failed to create light of type ${subtype}`);
        return prev;
      }

      return {
        ...prev,
        worlds: {
          ...prev.worlds,
          [activeWorld.id]: {
            ...activeWorld,
            items: {
              ...activeWorld.items,
              [id]: { ...newLight, id }
            }
          }
        },
        selectedItemId: id
      };
    });
  };

  /**
   * Adds an effect to the active world
   * 
   * @param subtype - The subtype of the effect to add
   */
  const addEffect = (subtype: string = 'bloom') => {
    if (!activeWorld) return;

    setState(prev => {
      const id = uuidv4();
      const effectCount = Object.values(activeWorld.items).filter(item => item.type === 'effect').length;
      const definitionType = `effect.${subtype}` as ItemDefinitionType;
      const newEffect = createItem(definitionType, `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} ${effectCount + 1}`);

      if (!newEffect) {
        console.error(`Failed to create effect of type ${subtype}`);
        return prev;
      }

      return {
        ...prev,
        worlds: {
          ...prev.worlds,
          [activeWorld.id]: {
            ...activeWorld,
            items: { ...activeWorld.items, [id]: { ...newEffect, id } }
          }
        },
        selectedItemId: id
      };
    });
  };

  /**
   * Adds a material to the active world
   * 
   * @param subtype - The subtype of the material to add
   */
  const addMaterial = (subtype: string = 'standard') => {
    if (!activeWorld) return;

    setState(prev => {
      const id = uuidv4();
      const materialCount = Object.values(activeWorld.items).filter(item => item.type === 'material').length;
      const definitionType = `material.${subtype}` as ItemDefinitionType;
      const newMaterial = createItem(definitionType, `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} Material ${materialCount + 1}`);

      if (!newMaterial) {
        console.error(`Failed to create material of type ${subtype}`);
        return prev;
      }

      return {
        ...prev,
        worlds: {
          ...prev.worlds,
          [activeWorld.id]: {
            ...activeWorld,
            items: {
              ...activeWorld.items,
              [id]: { ...newMaterial, id }
            }
          }
        },
        selectedItemId: id
      };
    });
  };

  /**
   * Adds a link to the active world
   * 
   * @param subtype - The subtype of the link to add
   */
  const addLink = (subtype: string = 'beat') => {
    if (!activeWorld) return;

    setState(prev => {
      const id = uuidv4();
      const linkCount = Object.values(activeWorld.items).filter(item => item.type === 'link').length;
      const definitionType = `link.${subtype}` as ItemDefinitionType;
      const newLink = createItem(definitionType, `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} Link ${linkCount + 1}`);

      if (!newLink) {
        console.error(`Failed to create link of type ${subtype}`);
        return prev;
      }

      return {
        ...prev,
        worlds: { 
          ...prev.worlds, 
          [activeWorld.id]: { 
            ...activeWorld, 
            items: { 
              ...activeWorld.items, 
              [id]: { ...newLink, id } 
            } 
          } 
        },
        selectedItemId: id
      };
    });
  };

  /**
   * Updates an item in the active world
   * 
   * @param itemId - The id of the item to update
   * @param changes - The changes to apply to the item
   */
  const updateItem = (itemId: string, changes: Partial<Item>) => {
    if (!activeWorld) return;

    setState(prev => {
      if (!(itemId in activeWorld.items)) return prev;

      const currentItem = activeWorld.items[itemId];
      
      // Special handling for link updates to prevent unnecessary deep merging
      if (currentItem.type === 'link') {
        // Check if this is a property-only update from the animation system
        if (changes.properties && Object.keys(changes).length === 1) {
          // For animation updates, we use a more targeted approach
          // that doesn't recreate the entire state tree
          const updatedItem = {
            ...currentItem,
            properties: {
              ...currentItem.properties,
              ...changes.properties
            },
            // Preserve the render function and THREE.js refs
            render: currentItem.render,
            threeRef: currentItem.threeRef,
          };

          // Create a new items object with just the updated item
          const updatedItems = {
            ...prev.worlds[activeWorld.id].items,
            [itemId]: updatedItem
          };

          // Create a new state with minimal object recreation
          return {
            ...prev,
            worlds: {
              ...prev.worlds,
              [activeWorld.id]: {
                ...prev.worlds[activeWorld.id],
                items: updatedItems
              }
            }
          };
        }
        
        // For non-property updates (like adding/removing actions), use the full update
        // Make sure to preserve the exact same threeRef object to prevent duplication
        const linkItem = currentItem as any; // Type as any to access actions
        const linkChanges = changes as any; // Type as any to access actions
        
        const updatedItem = {
          ...currentItem,
          ...changes,
          // Explicitly preserve the render function and THREE.js refs
          render: currentItem.render,
          threeRef: currentItem.threeRef,
          // Preserve actions if they're not being updated
          actions: linkChanges.actions || linkItem.actions
        };

        return {
          ...prev,
          worlds: {
            ...prev.worlds,
            [activeWorld.id]: {
              ...activeWorld,
              items: {
                ...activeWorld.items,
                [itemId]: updatedItem
              }
            }
          }
        };
      }

      // Handle transform updates by creating new instances
      let updatedProperties: { properties?: any } = {};
      if (changes.properties) {
        // Check if this is a simple property update that doesn't need deep merging
        const isSimpleUpdate = changes.properties && 
                              !changes.properties.transform && 
                              Object.keys(changes.properties).every(key => 
                                typeof changes.properties![key] !== 'object' || 
                                changes.properties![key] === null);

        if (isSimpleUpdate) {
          // For simple updates, just merge the properties
          updatedProperties = {
            properties: {
              ...currentItem.properties,
              ...changes.properties,
            }
          };
        } else {
          // For complex updates with nested objects, do a more careful merge
          updatedProperties = {
            properties: {
              ...currentItem.properties,
              ...changes.properties,
            }
          };

          // Special handling for transform to create new instances
          if (changes.properties.transform) {
            const transform = changes.properties.transform;
            updatedProperties.properties.transform = {
              position: transform.position ? new THREE.Vector3().copy(transform.position) : currentItem.properties.transform?.position,
              rotation: transform.rotation ? new THREE.Euler().copy(transform.rotation) : currentItem.properties.transform?.rotation,
              scale: transform.scale ? new THREE.Vector3().copy(transform.scale) : currentItem.properties.transform?.scale
            };
          }
        }
      }

      // Create updated item while preserving the render function and THREE.js refs
      const updatedItem = {
        ...currentItem,
        ...changes,
        ...updatedProperties,
        // Explicitly preserve the render function and THREE.js refs
        render: currentItem.render,
        threeRef: currentItem.threeRef,
      };

      // Create a new items object with just the updated item
      const updatedItems = {
        ...prev.worlds[activeWorld.id].items,
        [itemId]: updatedItem
      };

      // Create a new state with minimal object recreation
      return {
        ...prev,
        worlds: {
          ...prev.worlds,
          [activeWorld.id]: {
            ...prev.worlds[activeWorld.id],
            items: updatedItems
          }
        }
      };
    });
  };

  /**
   * Deletes an item from the active world
   * 
   * @param itemId - The id of the item to delete
   */
  const deleteItem = (itemId: string) => {
    if (!activeWorld) return;

    setState(prev => {
      if (!(itemId in activeWorld.items)) return prev;

      // Create new items object without the deleted item
      const { [itemId]: deletedItem, ...remainingItems } = activeWorld.items;

      // If we're deleting the selected item, clear the selection
      const newSelectedItemId = state.selectedItemId === itemId ? null : state.selectedItemId;

      return {
        ...prev,
        worlds: {
          ...prev.worlds,
          [activeWorld.id]: {
            ...activeWorld,
            items: remainingItems
          }
        },
        selectedItemId: newSelectedItemId
      };
    });
  };

  /**
   * Deletes a child item from a parent item
   * 
   * @param parentId - The id of the parent item
   * @param childType - The type of child item ('linkAction', 'groupItem', etc.)
   * @param childIndex - The index of the child item to delete
   */
  const deleteChildItem = (parentId: string, childType: string, childIndex: number) => {
    if (!activeWorld) return;

    setState(prev => {
      if (!(parentId in activeWorld.items)) return prev;

      const parentItem = activeWorld.items[parentId];
      
      // Handle different child types
      if (childType === 'linkAction' && parentItem.type === 'link') {
        // For link actions
        const link = parentItem as any; // Type as any to access actions
        if (!link.actions || childIndex >= link.actions.length) return prev;
        
        // Create a new actions array without the deleted action
        const updatedActions = [...link.actions];
        updatedActions.splice(childIndex, 1);
        
        // Update the parent link with the new actions array
        const updatedLink = {
          ...link,
          actions: updatedActions
        };
        
        return {
          ...prev,
          worlds: {
            ...prev.worlds,
            [activeWorld.id]: {
              ...activeWorld,
              items: {
                ...activeWorld.items,
                [parentId]: updatedLink
              }
            }
          }
        };
      }
      
      // For future child types (like group items)
      // Add handling for other child types here
      
      return prev;
    });
  };

  const value = {
    state,
    setState,
    createWorld,
    setActiveWorld,
    setActiveMixer,
    selectItem,
    addMesh,
    addLight,
    addEffect,
    addMaterial,
    addLink,
    updateItem,
    deleteItem,
    deleteChildItem,
    activeWorld,
    selectedItem,
    definitions,
    getDefinition,
    createItem,
    clock,
    startClock,
    stopClock,
    setBpm,
    setMeasure
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}; 